import React, { useState } from 'react';
import './ApiEndpointCard.scss';

const ApiEndpointCard = ({ endpoint }) => {
  const [copiedSection, setCopiedSection] = useState(null);

  const copyToClipboard = async (text, section) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const formatCodeBlock = (content) => {
    if (typeof content === 'string') {
      return content;
    }
    return JSON.stringify(content, null, 2);
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'success',
      POST: 'warning', 
      PUT: 'info',
      PATCH: 'accent',
      DELETE: 'error'
    };
    return colors[method.toUpperCase()] || 'accent';
  };

  const getStatusCodeColor = (code) => {
    const codeNum = parseInt(code);
    if (codeNum >= 200 && codeNum < 300) return 'success';
    if (codeNum >= 300 && codeNum < 400) return 'info';
    if (codeNum >= 400 && codeNum < 500) return 'warning';
    if (codeNum >= 500) return 'error';
    return 'accent';
  };

  return (
    <div className="api-endpoint-card">
      {/* Header */}
      <div className="endpoint-header">
        <div className="endpoint-title-section">
          <div className="endpoint-method-path">
            <span className={`method-badge ${getMethodColor(endpoint.method)}`}>
              {endpoint.method}
            </span>
            <code className="endpoint-path">{endpoint.path}</code>
          </div>
          <h2 className="endpoint-title">{endpoint.title}</h2>
        </div>
      </div>

      {/* Description */}
      <div className="endpoint-description">
        <p>{endpoint.description}</p>
      </div>

      {/* Authentication */}
      <div className="section">
        <h3 className="section-title">Authentication</h3>
        <div className="auth-requirement">
          <span className="auth-badge required">Required</span>
          <p>Include your API key in the request header:</p>
        </div>
        <div className="code-block-container">
          <div className="code-block-header">
            <span>Headers</span>
            <button 
              className="copy-button"
              onClick={() => copyToClipboard('x-api-key: YOUR_API_KEY', 'auth')}
            >
              {copiedSection === 'auth' ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <pre className="code-block">
            <code>x-api-key: YOUR_API_KEY</code>
          </pre>
        </div>
      </div>

      {/* Request */}
      <div className="section">
        <h3 className="section-title">Request</h3>
        
        {/* HTTP Request Example */}
        <div className="code-block-container">
          <div className="code-block-header">
            <span>HTTP Request</span>
            <button 
              className="copy-button"
              onClick={() => {
                const queryString = endpoint.request.queryParameters ? 
                  '?' + Object.entries(endpoint.request.queryParameters).map(([key, value]) => `${key}=${value}`).join('&') : '';
                const requestText = `${endpoint.method} ${endpoint.path}${queryString}\n\nHeaders:\n${Object.entries(endpoint.request.headers).map(([key, value]) => `${key}: ${value}`).join('\n')}${endpoint.request.queryParameters ? `\n\nQuery Parameters:\n${Object.entries(endpoint.request.queryParameters).map(([key, value]) => `${key}=${value}`).join('\n')}` : ''}${endpoint.request.body ? `\n\nBody:\n${formatCodeBlock(endpoint.request.body)}` : ''}`;
                copyToClipboard(requestText, 'request');
              }}
            >
              {copiedSection === 'request' ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <pre className="code-block">
            <code>
              {(() => {
                const queryString = endpoint.request.queryParameters ? 
                  '?' + Object.entries(endpoint.request.queryParameters).map(([key, value]) => `${key}=${value}`).join('&') : '';
                return `${endpoint.method} ${endpoint.path}${queryString}

Headers:`;
              })()}
              {Object.entries(endpoint.request.headers).map(([key, value]) => (
                <div key={key}>{`${key}: ${value}`}</div>
              ))}
              {endpoint.request.queryParameters && (
                <>
                  {`

Query Parameters:`}
                  {Object.entries(endpoint.request.queryParameters).map(([key, value]) => (
                    <div key={key}>{`${key}=${value}`}</div>
                  ))}
                </>
              )}
              {endpoint.request.body && (
                <>
                  {`

Body:`}
                  <div>{formatCodeBlock(endpoint.request.body)}</div>
                </>
              )}
            </code>
          </pre>
        </div>

        {/* cURL Example */}
        <div className="code-block-container">
          <div className="code-block-header">
            <span>cURL Example</span>
            <button 
              className="copy-button"
              onClick={() => {
                const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace('api/v1', 'external/v1') || 'http://localhost:3000/external/v1';
                const queryString = endpoint.request.queryParameters ? 
                  '?' + Object.entries(endpoint.request.queryParameters).map(([key, value]) => `${key}=${value}`).join('&') : '';
                const url = `${baseUrl}${endpoint.path}${queryString}`;
                const headers = Object.entries(endpoint.request.headers).map(([key, value]) => `  --header '${key}: ${value === 'YOUR_API_KEY' ? 'YOUR_API_KEY' : value}' \\`).join('\n');
                const curlCommand = `curl --request ${endpoint.method} \\\n  --url '${url}' \\\n${headers}${endpoint.request.body ? `\n  --data '${JSON.stringify(endpoint.request.body)}'` : ''}`;
                copyToClipboard(curlCommand, 'curl');
              }}
            >
              {copiedSection === 'curl' ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <pre className="code-block">
            <code>
              {(() => {
                const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace('api/v1', 'external/v1') || 'http://localhost:3000/external/v1';
                const queryString = endpoint.request.queryParameters ? 
                  '?' + Object.entries(endpoint.request.queryParameters).map(([key, value]) => `${key}=${value}`).join('&') : '';
                const url = `${baseUrl}${endpoint.path}${queryString}`;
                return `curl --request ${endpoint.method} \\
  --url '${url}' \\`;
              })()}
              {Object.entries(endpoint.request.headers).map(([key, value]) => (
                <div key={key}>{`  --header '${key}: ${value === 'YOUR_API_KEY' ? 'YOUR_API_KEY' : value}' \\`}</div>
              ))}
              {endpoint.request.body && (
                <div>{`  --data '${JSON.stringify(endpoint.request.body)}'`}</div>
              )}
            </code>
          </pre>
        </div>
      </div>

      {/* Parameters */}
      {endpoint.parameters && endpoint.parameters.length > 0 && (
        <div className="section">
          <h3 className="section-title">Parameters</h3>
          <div className="parameters-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {endpoint.parameters.map((param, index) => (
                  <tr key={index}>
                    <td>
                      <code className="param-name">{param.name}</code>
                    </td>
                    <td>
                      <span className="param-type">{param.type}</span>
                    </td>
                    <td>
                      <span className={`required-badge ${param.required ? 'required' : 'optional'}`}>
                        {param.required ? 'Required' : 'Optional'}
                      </span>
                    </td>
                    <td className="param-description">{param.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Response */}
      <div className="section">
        <h3 className="section-title">Response</h3>
        
        {/* Success Response */}
        <div className="code-block-container">
          <div className="code-block-header">
            <span>Success Response ({endpoint.response.success.code})</span>
            <button 
              className="copy-button"
              onClick={() => copyToClipboard(formatCodeBlock(endpoint.response.success.content), 'response-success')}
            >
              {copiedSection === 'response-success' ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <pre className="code-block">
            <code>{formatCodeBlock(endpoint.response.success.content)}</code>
          </pre>
        </div>

        {/* Error Response (if exists) */}
        {endpoint.response.error && (
          <div className="code-block-container error-response">
            <div className="code-block-header">
              <span>Error Response ({endpoint.response.error.code})</span>
              <button 
                className="copy-button"
                onClick={() => copyToClipboard(formatCodeBlock(endpoint.response.error.content), 'response-error')}
              >
                {copiedSection === 'response-error' ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <pre className="code-block">
              <code>{formatCodeBlock(endpoint.response.error.content)}</code>
            </pre>
          </div>
        )}

        {/* Throttled Response (if exists) */}
        {endpoint.response.throttled && (
          <div className="code-block-container throttled-response">
            <div className="code-block-header">
              <span>Throttled Response ({endpoint.response.throttled.code})</span>
              <button 
                className="copy-button"
                onClick={() => copyToClipboard(formatCodeBlock(endpoint.response.throttled.content), 'response-throttled')}
              >
                {copiedSection === 'response-throttled' ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <pre className="code-block">
              <code>{formatCodeBlock(endpoint.response.throttled.content)}</code>
            </pre>
          </div>
        )}
      </div>

      {/* Status Codes */}
      <div className="section">
        <h3 className="section-title">Status Codes</h3>
        <div className="status-codes-list">
          {endpoint.statusCodes.map((status, index) => (
            <div key={index} className="status-code-item">
              <span className={`status-code ${getStatusCodeColor(status.code)}`}>
                {status.code}
              </span>
              <span className="status-description">{status.description}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ApiEndpointCard;
